import React, { useState, useRef, useEffect } from 'react';
// import EmptyState_Bin from '../Images/EmptyState_Image.png';
// import EmptyState_BinDark from '../Images/EmptyState_ImageDark.png';
// import fallbackSvg from '../fallBackSvg.svg';
// import fallbackSvgDark from '../fallBackSvgDark.svg';
import { useColorMode, useColorModeValue } from '@chakra-ui/color-mode';
import useStore from '../stores/store';
import { Popover, PopoverContent, PopoverTrigger } from '@chakra-ui/popover';
import { Portal } from '@chakra-ui/portal';
import { Button, IconButton } from '@chakra-ui/button';
import { AlarmIcon, IconWrapper, TrashIcon } from './UiElements';
import { useRemindersStore } from '../stores/remindersStore';
import { CloseButton } from '@chakra-ui/close-button';
import { Box, Flex, Link, Text } from '@chakra-ui/layout';
import { Input } from '@chakra-ui/input';
import useCustomToast from './ToastProvider';
import { Tooltip } from '@chakra-ui/react';
import { tooltipAnimation } from './customTheme';

interface RemindersPopoverProps {
    // Define your prop types here
    isOpen: boolean;
    setIsOpen: (boolean) => void;
    content: Object;
    hasAlarm: any;
}

export const ReminderPopover: React.FC<RemindersPopoverProps> = ({
    isOpen,
    setIsOpen,
    content,
    hasAlarm
}) => {
    const { colorMode } = useColorMode();
    const pickerBg = useColorModeValue('rgba(244, 245, 247, 1)', '#212528');
    const text = useColorModeValue('#1A202C', '#FFF');
    const linkShadow = useColorModeValue(
        '2px 5px 35px 0 rgba(0,0,0,0.40)',
        '0px 14px 54px 0 rgba(0,0,0,0.90)'
    );
    const iconDarkerColor = useColorModeValue('#4A5568', '#B0B7C1');

    const open = () => setIsOpen(true);
    const close = () => setIsOpen(false);

    const userData = useStore.getState().userData;
    let userId = userData.userId;
    const hasAlarmWithId =
        hasAlarm && hasAlarm?.includes(userId) ? true : false;

    return (
        <Popover isOpen={isOpen} onClose={close} closeOnBlur={false}>
            <PopoverTrigger>
                <Button
                    leftIcon={
                        <svg
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 20 20"
                            style={{
                                width: '16px',
                                height: '16px'
                            }}
                        >
                            <path
                                fill={
                                    hasAlarmWithId ? '#3793ff' : iconDarkerColor
                                }
                                d="M10 2a6 6 0 00-6 6v3.586l-.707.707A1 1 0 004 14h12a1 1 0 00.707-1.707L16 11.586V8a6 6 0 00-6-6zM10 18a3 3 0 01-3-3h6a3 3 0 01-3 3z"
                            />
                        </svg>
                    }
                    onClick={isOpen ? close : open}
                    color={hasAlarmWithId ? '#3793ff' : ''}
                    mr={3}
                    transition="0.12s"
                    rounded="8px"
                >
                    Reminder
                </Button>
            </PopoverTrigger>
            <PopoverContent
                zIndex={5001}
                rounded="12px"
                borderWidth="0px"
                //overflow="hidden"
                _focus={{ outline: 'none' }}
                boxShadow={linkShadow}
                //ref={ref}
                p={'12px 0'}
                bg={pickerBg}
                border={colorMode === 'dark' ? 'none' : ''}
                color={text}
                onMouseDown={(e) => e.preventDefault()}
                maxW={'360px'}
                minW={'360px'}
            >
                <ReminderPanel
                    close={close}
                    content={content}
                    callBackAfterSet={close}
                    hasAlarmWithId={hasAlarmWithId}
                />
            </PopoverContent>
        </Popover>
    );
};

export const ReminderPanel = ({
    close,
    content,
    callBackAfterSet,
    hasAlarmWithId
}) => {
    //@ts-ignore
    const newReminder = useRemindersStore((state) => state.newReminder);
    //@ts-ignore
    const remindersData = useRemindersStore((state) => state.remindersData);
    //@ts-ignore
    const clearReminder = useRemindersStore((state) => state.clearReminder);
    const showToast = useCustomToast();
    //colorstuff
    const { colorMode } = useColorMode();
    const iconDarkerColor = useColorModeValue('gray.600', '#FFF');
    const icon = useColorModeValue('gray.500', '#A1A2A4');
    const bodyBg = useColorModeValue('#FFF', '#262A2D');
    const text = useColorModeValue('#1A202C', '#FFF');
    const linkColor = useColorModeValue('#3690fb', '#3690fb');
    //state and logic
    //const [isOpen, setIsOpen] = useState(false);

    const existingReminder = hasAlarmWithId
        ? Array.isArray(remindersData) &&
          remindersData.find((item) => item.id === content.id)
        : null;
    // Extract date and time if reminder exists
    const initialDate = existingReminder
        ? existingReminder.date.split(' ')[0]
        : '';
    const initialTime = existingReminder
        ? existingReminder.date.split(' ')[1]
        : '';

    // Default values
    let initialTab = 'onetime';
    let initialRadio = 'weekly';
    let initialSelectedDays = [];
    let initialDayNumber = 1;

    if (existingReminder && existingReminder.repeat) {
        initialTab = 'repeat';

        if (
            existingReminder.selectedDays &&
            existingReminder.selectedDays.length > 0
        ) {
            initialRadio = 'weekly';
            initialSelectedDays = existingReminder.selectedDays;
        } else if (typeof existingReminder.dayNumberToRepeat === 'number') {
            initialRadio = 'monthly';
            initialDayNumber = existingReminder.dayNumberToRepeat;
        }
    }

    const [date, setDate] = useState(initialDate);
    const [time, setTime] = useState(initialTime);
    const [tabChosen, setTabValue] = useState(initialTab);
    const [radioVal, setRadio] = useState(initialRadio);
    const [selectedDays, setSelectedDays] = useState(initialSelectedDays);
    const [dayNumberToRepeat, setDayNumber] = useState(initialDayNumber);

    const handleDateChange = (event) => setDate(event.target.value);
    const handleTimeChange = (event) => setTime(event.target.value);

    const closeMiddle = () => {
        close();
        callBackAfterSet();
    };

    const radioRef = useRef();
    const focusRadio = () => {
        radioRef.current && (radioRef.current as HTMLInputElement).focus();
    };

    const setRadioMiddle = (val) => {
        setRadio(val);
        setError(null);
    };

    const clearReminderMiddle = () => {
        if (existingReminder) {
            clearReminder(content.id, existingReminder.content.WSid);
            const dateTimeString = `${date} ${time}`;
            showToast({
                title: 'Reminder cleared',
                subtitle: 'Date: ' + dateTimeString,
                type: 'info'
            });
            callBackAfterSet();
        }
    };

    const getFormattedDateAndTime = (daysToAdd) => {
        let currentDate = new Date();
        let futureDate = new Date(currentDate);
        futureDate.setDate(futureDate.getDate() + daysToAdd);

        // Date formatting
        let dd = String(futureDate.getDate()).padStart(2, '0');
        let mm = String(futureDate.getMonth() + 1).padStart(2, '0');
        let yyyy = futureDate.getFullYear();
        let formattedDate = yyyy + '-' + mm + '-' + dd;

        // Time formatting
        let hh = String(currentDate.getHours()).padStart(2, '0');
        let min = String(currentDate.getMinutes()).padStart(2, '0');
        let formattedTime = hh + ':' + min;

        return { formattedDate, formattedTime };
    };

    const preButtons = [
        {
            name: '+1d',
            callback: () => {
                const { formattedDate, formattedTime } =
                    getFormattedDateAndTime(1);
                setDate(formattedDate);
                setTime(formattedTime);
            }
        },
        {
            name: '+2d',
            callback: () => {
                const { formattedDate, formattedTime } =
                    getFormattedDateAndTime(2);
                setDate(formattedDate);
                setTime(formattedTime);
            }
        },
        {
            name: '+3d',
            callback: () => {
                const { formattedDate, formattedTime } =
                    getFormattedDateAndTime(3);
                setDate(formattedDate);
                setTime(formattedTime);
            }
        },
        {
            name: '+1w',
            callback: () => {
                const { formattedDate, formattedTime } =
                    getFormattedDateAndTime(7);
                setDate(formattedDate);
                setTime(formattedTime);
            }
        },
        {
            name: '+2w',
            callback: () => {
                const { formattedDate, formattedTime } =
                    getFormattedDateAndTime(14);
                setDate(formattedDate);
                setTime(formattedTime);
            }
        }
    ];

    const repeatDays = [
        {
            name: 'Mon',
            callback: () => {
                //console.log('mon');
            }
        },
        {
            name: 'Tue',
            callback: () => {
                //console.log('tue');
            }
        },
        {
            name: 'Wed',
            callback: () => {
                //console.log('wed');
            }
        },
        {
            name: 'Thu',
            callback: () => {
                //console.log('thu');
            }
        },
        {
            name: 'Fri',
            callback: () => {
                //console.log('fri');
            }
        },
        {
            name: 'Sat',
            callback: () => {
                //console.log('sat');
            }
        },
        {
            name: 'Sun',
            callback: () => {
                //console.log('sun');
            }
        }
    ];

    const toggleDay = (dayName) => {
        setSelectedDays((prevSelectedDays) => {
            if (prevSelectedDays.length === 0) {
                // If no days were previously selected, set the current time
                const now = new Date();
                const hours = String(now.getHours()).padStart(2, '0');
                const minutes = String(now.getMinutes()).padStart(2, '0');
                const formattedTime = `${hours}:${minutes}`;

                setTime(formattedTime);
            }

            if (prevSelectedDays.includes(dayName)) {
                return prevSelectedDays.filter((day) => day !== dayName);
            } else {
                return [...prevSelectedDays, dayName];
            }
        });
    };

    const [errors, setError] = useState(null);

    const submit = () => {
        if (tabChosen === 'onetime') {
            if (!date || !time) {
                setError('Both date and time must be provided.');
                return;
            }
            //add error handle to prevent empty date or time input
            const dateTimeString = `${date} ${time}`; // Format: YYYY-MM-DD HH:MM
            newReminder(dateTimeString, content, false, hasAlarmWithId);
            showToast({
                title: hasAlarmWithId ? 'Reminder updated' : 'Reminder set',
                subtitle: 'To ' + dateTimeString,
                type: 'success'
            });
            callBackAfterSet();
        } else {
            if (!time) {
                setError('Time must be provided.');
                return;
            }
            // Today's date
            const today = new Date();
            const currentDay = today.getDay(); // Sunday - 0, Monday - 1, etc.
            const currentDate = today.getDate();

            // Determine the type of repeating reminder based on radioVal.
            if (radioVal === 'weekly') {
                // If it's a weekly reminder, use selectedDays for repetition.
                if (!selectedDays || selectedDays.length === 0) {
                    setError(
                        'Please select at least one day for weekly repetition.'
                    );
                    return;
                }

                // Current time
                const currentTime = `${today.getHours()}:${today.getMinutes()}:${today.getSeconds()}`;

                // Find the next occurrence of the selected days.
                const daysMapping = [
                    'Sun',
                    'Mon',
                    'Tue',
                    'Wed',
                    'Thu',
                    'Fri',
                    'Sat'
                ];
                const nextDay = selectedDays
                    .sort(
                        (a, b) =>
                            daysMapping.indexOf(a) - daysMapping.indexOf(b)
                    )
                    .find((day) => daysMapping.indexOf(day) >= currentDay); // Changed > to >=

                const nextDate = new Date(today);

                if (nextDay) {
                    const daysToAdd = daysMapping.indexOf(nextDay) - currentDay;
                    nextDate.setDate(today.getDate() + daysToAdd);

                    // If the next day is today and the selected time is later than current time
                    if (daysToAdd === 0 && time > currentTime) {
                        // No change in nextDate, keep it as today.
                    } else {
                        nextDate.setDate(
                            today.getDate() +
                                (daysMapping.indexOf(nextDay) - currentDay)
                        );
                    }
                } else {
                    // If no next day is found in this week, pick the first day of the next week.
                    nextDate.setDate(
                        today.getDate() +
                            (7 -
                                currentDay +
                                daysMapping.indexOf(selectedDays[0]))
                    );
                }

                const dateTimeString = `${
                    nextDate.toISOString().split('T')[0]
                } ${time}`;
                newReminder(
                    dateTimeString,
                    content,
                    true,
                    hasAlarmWithId,
                    selectedDays,
                    null
                );
                showToast({
                    title: hasAlarmWithId ? 'Reminder updated' : 'Reminder set',
                    subtitle: 'First up ' + dateTimeString,
                    type: 'success'
                });
            } else if (radioVal === 'monthly') {
                // If it's a monthly reminder, use dayNumberToRepeat for repetition.
                if (!dayNumberToRepeat) {
                    setError(
                        'Please select a day number for monthly repetition.'
                    );
                    return;
                }

                // Find the next occurrence of the selected day number.
                const nextDate = new Date(today);
                let todayTime = today.getTime();

                if (
                    dayNumberToRepeat > currentDate ||
                    (dayNumberToRepeat === currentDate &&
                        new Date(
                            `${today.toISOString().split('T')[0]} ${time}`
                        ).getTime() > todayTime)
                ) {
                    nextDate.setDate(dayNumberToRepeat);
                } else {
                    // If the day has passed in this month or is today but the time has passed, set the reminder for the next month.
                    nextDate.setMonth(today.getMonth() + 1);
                    nextDate.setDate(dayNumberToRepeat);
                }

                // Check if the month has the selected day.
                const lastDayOfNextMonth = new Date(
                    nextDate.getFullYear(),
                    nextDate.getMonth() + 1,
                    0
                ).getDate();

                if (dayNumberToRepeat > lastDayOfNextMonth) {
                    // If the next month doesn't have the desired day, you can decide on the desired behavior.
                    // This example sets the reminder to the last day of the next month.
                    nextDate.setDate(lastDayOfNextMonth);
                }

                const dateTimeString = `${
                    nextDate.toISOString().split('T')[0]
                } ${time}`;
                newReminder(
                    dateTimeString,
                    content,
                    true,
                    hasAlarmWithId,
                    null,
                    dayNumberToRepeat
                );
                showToast({
                    title: hasAlarmWithId ? 'Reminder updated' : 'Reminder set',
                    subtitle: 'First up ' + dateTimeString,
                    type: 'success'
                });
            } else {
                setError('Invalid repeating option selected.');
                return;
            }
            callBackAfterSet();
        }

        close();
    };

    const initialDateTime = existingReminder ? existingReminder.date : '';

    const isValid =
        tabChosen === 'onetime'
            ? existingReminder
                ? date + ' ' + time !== initialDateTime
                : date !== '' && time !== ''
            : existingReminder
            ? time !== initialTime ||
              radioVal !== initialRadio ||
              JSON.stringify(selectedDays) !==
                  JSON.stringify(initialSelectedDays) ||
              dayNumberToRepeat !== initialDayNumber
            : time !== '';

    const toggleTypeOfReminder = (index) => {
        if (index === 0) {
            setTabValue('onetime');
        } else {
            setTabValue('repeat');
        }
    };

    /* const userState = useStore((state) => state.userState);
    const activePlan =
        userState.stripeSubscriptionStatus &&
        userState.stripeSubscriptionStatus === 'active'
            ? true
            : false; */
    let activePlan = remindersData !== 'noActivePlan';

    return (
        <>
            <CloseButton
                mt="4px"
                color={icon}
                transition="0s"
                onClick={close}
                size="sm"
                ml={'auto'}
                mr="16px"
            />
            <Box
                /* bg={bodyBg} */
                /* px="16px" */
                pb="8px"
                rounded="8px"
                mt="0px"
                onClick={(e) => {
                    e.stopPropagation();
                }}
            >
                <Button
                    onClick={() => toggleTypeOfReminder(0)}
                    variant={tabChosen === 'onetime' ? 'solid' : 'ghost'}
                    transition="0.12s"
                    color="#3690fb"
                    ml="16px"
                    roundedTop={'10px'}
                    roundedBottom={0}
                    bg={tabChosen === 'onetime' ? bodyBg : 'transparent'}
                    _hover={{
                        bg: tabChosen === 'onetime' ? bodyBg : ''
                    }}
                    _active={{ bg: 'var(--chakra-colors-chakra-border-color)' }}
                    borderTop={
                        tabChosen === 'onetime'
                            ? 'var(--chakra-colors-teBorder)'
                            : '1px solid transparent'
                    }
                    borderLeft={
                        tabChosen === 'onetime'
                            ? 'var(--chakra-colors-teBorder)'
                            : '1px solid transparent'
                    }
                    borderRight={
                        tabChosen === 'onetime'
                            ? 'var(--chakra-colors-teBorder)'
                            : '1px solid transparent'
                    }
                    mb="-1px"
                    fontSize={'14px'}
                >
                    One time
                </Button>
                <Button
                    ml="8px"
                    onClick={() => toggleTypeOfReminder(1)}
                    variant={tabChosen === 'repeat' ? 'solid' : 'ghost'}
                    transition="0.12s"
                    color="#3690fb"
                    roundedTop={'10px'}
                    roundedBottom={0}
                    bg={tabChosen === 'repeat' ? bodyBg : 'transparent'}
                    _hover={{
                        bg: tabChosen === 'repeat' ? bodyBg : ''
                    }}
                    _active={{ bg: 'var(--chakra-colors-chakra-border-color)' }}
                    borderTop={
                        tabChosen === 'repeat'
                            ? 'var(--chakra-colors-teBorder)'
                            : '1px solid transparent'
                    }
                    borderLeft={
                        tabChosen === 'repeat'
                            ? 'var(--chakra-colors-teBorder)'
                            : '1px solid transparent'
                    }
                    borderRight={
                        tabChosen === 'repeat'
                            ? 'var(--chakra-colors-teBorder)'
                            : '1px solid transparent'
                    }
                    mb="-1px"
                    fontSize={'14px'}
                >
                    Repeating
                </Button>
                <Box
                    bg={bodyBg}
                    px="16px"
                    pt={tabChosen === 'onetime' ? '12px' : '8px'}
                    pb="16px"
                    borderTop="var(--chakra-colors-teBorder)"
                    borderBottom="var(--chakra-colors-teBorder)"
                >
                    {tabChosen === 'onetime' ? (
                        <>
                            <Flex
                                mt="8px"
                                mb="16px"
                                style={{ gap: '8px', flexWrap: 'nowrap' }}
                            >
                                {preButtons.map((bt, index) => (
                                    <Button
                                        size="sm"
                                        key={index}
                                        onClick={bt.callback}
                                        rounded="8px"
                                        style={{ flexGrow: 1 }}
                                        transition={'0.12s'}
                                        border="var(--chakra-colors-teBorder)"
                                    >
                                        {bt.name}
                                    </Button>
                                ))}
                            </Flex>
                            <Input
                                type="date"
                                style={{ colorScheme: colorMode }}
                                value={date}
                                onChange={handleDateChange}
                            />
                            <Input
                                type="time"
                                style={{ colorScheme: colorMode }}
                                mt="4px"
                                value={time}
                                onChange={handleTimeChange}
                            />
                            {errors && (
                                <Flex>
                                    <Text
                                        fontSize={'12px'}
                                        color={'red.500'}
                                        mt="4px"
                                    >
                                        {errors}
                                    </Text>
                                </Flex>
                            )}
                        </>
                    ) : (
                        <>
                            <Flex mt="8px" mb="12px">
                                <Button
                                    border={
                                        radioVal === 'weekly'
                                            ? 'var(--chakra-colors-teBorder)'
                                            : '1px solid transparent'
                                    }
                                    _hover={{
                                        border: 'var(--chakra-colors-teBorder)'
                                    }}
                                    size="sm"
                                    onClick={() => setRadioMiddle('weekly')}
                                    variant={
                                        radioVal === 'weekly'
                                            ? 'solid'
                                            : 'ghost'
                                    }
                                    rounded="8px"
                                >
                                    Weekly
                                </Button>
                                <Button
                                    border={
                                        radioVal === 'monthly'
                                            ? 'var(--chakra-colors-teBorder)'
                                            : '1px solid transparent'
                                    }
                                    _hover={{
                                        border: 'var(--chakra-colors-teBorder)'
                                    }}
                                    ml="8px"
                                    size="sm"
                                    onClick={() => setRadioMiddle('monthly')}
                                    variant={
                                        radioVal === 'monthly'
                                            ? 'solid'
                                            : 'ghost'
                                    }
                                    rounded="8px"
                                >
                                    Monthly
                                </Button>
                            </Flex>
                            <Flex
                                mt={radioVal === 'weekly' ? '8px' : '0px'}
                                mb={radioVal === 'weekly' ? '16px' : '8px'}
                                style={{ gap: '8px', flexWrap: 'nowrap' }}
                            >
                                {radioVal === 'weekly' ? (
                                    repeatDays.map((day, index) => (
                                        <Button
                                            border="var(--chakra-colors-teBorder)"
                                            colorScheme={
                                                selectedDays.includes(day.name)
                                                    ? 'blue'
                                                    : 'gray'
                                            }
                                            size="sm"
                                            key={index}
                                            onClick={() => {
                                                day.callback();
                                                toggleDay(day.name);
                                            }}
                                            rounded="8px"
                                            style={{ flexGrow: 1 }}
                                            transition="0.12s"
                                        >
                                            {day.name}
                                        </Button>
                                    ))
                                ) : (
                                    <Flex flex="1">
                                        <Text fontSize={'16px'} my="auto">
                                            Day number:{' '}
                                        </Text>
                                        <Stepper
                                            dayNumberToRepeat={
                                                dayNumberToRepeat
                                            }
                                            setDayNumber={setDayNumber}
                                        />
                                    </Flex>
                                )}
                            </Flex>
                            <Input
                                type="time"
                                style={{ colorScheme: colorMode }}
                                mt="4px"
                                value={time}
                                onChange={handleTimeChange}
                            />
                            {errors && (
                                <Flex>
                                    <Text
                                        fontSize={'12px'}
                                        color={'red.500'}
                                        mt="4px"
                                    >
                                        {errors}
                                    </Text>
                                </Flex>
                            )}
                        </>
                    )}
                </Box>
                {!activePlan && (
                    <Text p="8px 16px 0px" color={text}>
                        Please{' '}
                        <Link
                            href="https://www.tabextend.com/pricing"
                            target="_blank"
                            rel="noopener noreferrer"
                            color={linkColor}
                            _hover={{
                                textDecoration: 'underline'
                            }}
                        >
                            upgrade your plan
                        </Link>{' '}
                        to enable reminders.
                    </Text>
                )}
                <Flex mt="16px" px="16px">
                    {/* <Tooltip
                        zIndex="500000"
                        placement="top"
                        rounded="6px"
                        motionProps={tooltipAnimation}
                        label="Clear reminder from item"
                    > */}
                    <IconButton
                        //size="sm"
                        aria-label="Clear reminder"
                        disabled={!existingReminder}
                        opacity={existingReminder ? '1' : '0.6'}
                        pointerEvents={existingReminder ? 'auto' : 'none'}
                        icon={<TrashIcon />}
                        onClick={clearReminderMiddle}
                        color={iconDarkerColor}
                        mr={3}
                        transition="0.12s"
                    />
                    {/* </Tooltip> */}
                    <Button
                        //size="sm"
                        cursor={'pointer'}
                        ml={'auto'}
                        onClick={submit}
                        color="#FFF"
                        opacity={isValid && activePlan ? '1' : '0.6'}
                        style={{
                            pointerEvents: activePlan ? 'auto' : 'none',
                            filter: activePlan ? 'none' : 'grayscale(10%)'
                        }}
                        backgroundImage={
                            'linear-gradient(145deg, #5DABFE 0%, #3391FF 100%)'
                        }
                        _hover={{
                            backgroundImage:
                                'linear-gradient(145deg, #3592FE 0%, #2E7EDB 100%)',
                            boxShadow: '0 10px 16px -8px rgba(49, 144, 255,0.5)'
                        }}
                        boxShadow="0 12px 18px -10px rgba(49, 144, 255,0.35)"
                        transition="all 0.12s cubic-bezier(.374,.019,.035,1.861)"
                        rounded="8px"
                    >
                        {existingReminder ? 'Update reminder' : 'Set reminder'}
                    </Button>
                </Flex>
            </Box>
        </>
    );
};

const Stepper = ({ dayNumberToRepeat, setDayNumber }) => {
    const { colorMode } = useColorMode();

    const incrementValue = () => {
        if (dayNumberToRepeat < 31) {
            setDayNumber((prevValue) => prevValue + 1);
        }
    };

    const decrementValue = () => {
        if (dayNumberToRepeat > 1) {
            setDayNumber((prevValue) => prevValue - 1);
        }
    };

    const handleInputChange = (event) => {
        const value = parseInt(event.target.value, 10);
        if (value >= 1 && value <= 31) {
            setDayNumber(value);
        }
    };

    const text = useColorModeValue('#1A202C', '#FFF');
    const bg = useColorModeValue('#FFF', '#262A2D');
    const borderColor = useColorModeValue(
        '1px solid #E6EAEF',
        '1px solid rgba(255, 255, 255, 0.16)'
    );
    const iconDarkerColor = useColorModeValue('gray.600', '#A1A2A4');

    return (
        <>
            <div style={{ display: 'inline-flex', flex: '1' }}>
                <input
                    type="number"
                    value={dayNumberToRepeat}
                    min="1"
                    max="31"
                    style={{
                        width: '54px',
                        marginLeft: 'auto',
                        userSelect: 'auto',
                        background: 'black',
                        color: text,
                        appearance: 'none',
                        fontSize: '1rem',
                        paddingLeft: '1rem',
                        paddingRight: '1rem',
                        height: '2.5rem',
                        borderRadius: '4px 0 0 4px',
                        border: borderColor,
                        backgroundColor: bg,
                        outline: 'none'
                    }}
                    onChange={handleInputChange}
                />
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                    <IconButton
                        aria-label="Increment value"
                        as={TriangleUp}
                        fontSize={'8px'}
                        size="xs"
                        color={iconDarkerColor}
                        onClick={incrementValue}
                        _focus={{ boxShadow: 'none' }}
                        style={{
                            cursor: 'pointer',
                            borderRadius: '0 4px 0 0',
                            display: 'flex',
                            alignItems: 'center',
                            flex: '1 1 0%',
                            transition: 'all 0.4s ease 0s',
                            userSelect: 'none',
                            lineHeight: 'normal',
                            border: borderColor
                        }}
                    >
                        +
                    </IconButton>
                    <IconButton
                        aria-label="Decrement value"
                        as={TriangleDown}
                        fontSize={'8px'}
                        size="xs"
                        color={iconDarkerColor}
                        onClick={decrementValue}
                        _focus={{ boxShadow: 'none' }}
                        style={{
                            cursor: 'pointer',
                            borderRadius: '0 0 4px 0',
                            display: 'flex',
                            alignItems: 'center',
                            flex: '1 1 0%',
                            transition: 'all 0.4s ease 0s',
                            userSelect: 'none',
                            lineHeight: 'normal',
                            border: borderColor
                        }}
                    >
                        -
                    </IconButton>
                </div>
            </div>
        </>
    );
};

const TriangleUp = () => {
    return (
        <svg
            viewBox="0 0 24 24"
            focusable="false"
            role="presentation"
            aria-hidden="true"
            className="css-1im46kq"
        >
            <path
                fill="currentColor"
                d="M12.8,5.4c-0.377-0.504-1.223-0.504-1.6,0l-9,12c-0.228,0.303-0.264,0.708-0.095,1.047 C2.275,18.786,2.621,19,3,19h18c0.379,0,0.725-0.214,0.895-0.553c0.169-0.339,0.133-0.744-0.095-1.047L12.8,5.4z"
            ></path>
        </svg>
    );
};

const TriangleDown = () => {
    return (
        <svg
            viewBox="0 0 24 24"
            focusable="false"
            role="presentation"
            aria-hidden="true"
            className="css-1im46kq"
        >
            <path
                fill="currentColor"
                d="M21,5H3C2.621,5,2.275,5.214,2.105,5.553C1.937,5.892,1.973,6.297,2.2,6.6l9,12 c0.188,0.252,0.485,0.4,0.8,0.4s0.611-0.148,0.8-0.4l9-12c0.228-0.303,0.264-0.708,0.095-1.047C21.725,5.214,21.379,5,21,5z"
            ></path>
        </svg>
    );
};
